import { inject } from "@angular/core";
import { catchError, defer, MonoTypeOperatorFunction, retry, timer } from "rxjs";
import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { Router } from "@angular/router";
import { HttpConcurrencyQueueFullError } from "../request-concurrency";

const FATAL_ERRORS = new Set([400, 401, 404, 409, 423, 429, 502]);

interface RetryContext {
    readonly count: number;
    readonly showBanner: boolean;
    readonly router: Router;
    readonly responseErrorHandlerService: ResponseErrorHandlerService;
}

export const httpHandlerInterceptor: HttpInterceptorFn = (req, next) => {
    const responseErrorHandlerService = inject(ResponseErrorHandlerService);
    const router = inject(Router);

    // Per-request, unlike the class interceptor this replaces: a single shared instance meant
    // concurrent requests could overwrite each other's banner preference mid-retry.
    const showBanner = req.headers.get("showBanner") !== "false";

    const clonedRequest = req.clone({
        headers: req.headers
            .set("Referrer-Policy", "strict-origin-when-cross-origin")
            .set(
                "Permissions-Policy",
                "geolocation=(), midi=(), notifications=(), push=(), sync-xhr=(), microphone=(), camera=(), magnetometer=(), gyroscope=(), speaker=(), vibrate=(), fullscreen=(), payment=()",
            ),
    });

    return next(clonedRequest).pipe(retryWithDelay({ count: 5, showBanner, router, responseErrorHandlerService }));
};

function retryWithDelay<T>(context: RetryContext): MonoTypeOperatorFunction<T> {
    const { count, showBanner, responseErrorHandlerService } = context;
    return (input) =>
        defer(() => {
            let retryStopped = false;
            return input.pipe(
                retry({
                    count,
                    delay: (error) => {
                        try {
                            handleAttemptFailure(error, context);
                        } catch (error_) {
                            retryStopped = true;
                            throw error_;
                        }
                        return timer(2 ** (6 - count) * 1000);
                    },
                }),
                catchError((error) => {
                    if (retryStopped) throw error;
                    handleAttemptFailure(error, context);
                    if (showBanner) {
                        responseErrorHandlerService.showBanner(
                            error.status === 0 ? { type: "lostConnection" } : { error: error.error, type: "microService" },
                        );
                    }
                    throw error;
                }),
            );
        });
}

function handleAttemptFailure(error: HttpErrorResponse | HttpConcurrencyQueueFullError, context: RetryContext): void {
    if (error instanceof HttpConcurrencyQueueFullError) throw error;
    if (error.ok) return;
    if (error.status === 503) {
        context.router.navigate([context.router.url]);
    } else if (FATAL_ERRORS.has(error.status)) {
        const url = error.url as string;
        if (!url.includes("connect/authorize") && !url.includes("negotiate") && (!url.includes("/cards") || error.status !== 404)) {
            context.responseErrorHandlerService.responseErrorHandler(error);
        }
        throw error;
    }
}
